import { Component, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Highcharts from 'highcharts';
import { IUser } from 'projects/common/src/app/interfaces';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { objectPathExists } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { DateTimePresentationPipe } from 'src/app/modules/core/pipes/date-time-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IChimneyProduction } from 'src/app/modules/widget/interfaces/chimney-production';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { ProductionByChimneyModalComponent } from './production-by-chimney-modal/production-by-chimney-modal.component';

@Component({
  selector: 'app-production-by-chimney',
  templateUrl: './production-by-chimney.component.html',
  styleUrls: ['./production-by-chimney.component.scss'],
})
export class ProductionByChimneyComponent extends DashboardWidgetChartComponent implements OnDestroy {
  public hasData = false;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private languageService: LanguageService,
    private modalService: NgbModal,
    protected screenViewService: ScreenViewService,
    private datePipe: DatePresentationPipe,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  /**
   * Draw chart Production by Chimney
   */
  public drawView(chimneyData?: IChimneyProduction[]): any {
    const openModal = this.openModalChimney;
    const modalSvc = this.modalService;
    const date = this.parameterService.parameters.item('date').value as string;
    // const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const companyId = String((this.parameterService.parameters.item('user-data').value as IUser).company.id);
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const user = this.parameterService.parameters.item('user-data').value;

    const shelvesLang = this.languageService.message('shelves');

    this.hasData = !!chimneyData.length;

    const buildedSeries = this.buildSeries(chimneyData);

    const data = {
      chart: {
        type: 'column',
        backgroundColor: '#F8F8F8',
        height: 450,
      },
      title: {
        text: '',
      },
      subtitle: {
        text: `${this.languageService.message('company')}: ${user.company.name},
              ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
              ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
              ${this.languageService.message('shift_')}: ${shift.name}`,
      },
      credits: {
        enabled: false,
      },
      xAxis: [
        {
          labels: {
            style: {
              color: '#707070',
            },
          },
          categories: chimneyData.map((v) => v.zoneName),
          title: {
            text: this.languageService.message('zone').toUpperCase(),
            yAxis: 20,
            style: {
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
        },
      ],
      yAxis: [
        {
          labels: {
            style: {
              color: '#707070',
            },
          },
          title: {
            text: this.languageService.message('shelves').toUpperCase(),
            yAxis: 20,
            style: {
              fontSize: 12,
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
        },
        {
          title: {
            text: '',
          },
          labels: {
            format: '{value}',
            style: {
              color: '#707070',
            },
          },
          opposite: true,
        },
      ],
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          borderWidth: 2,
          borderColor: '#22D983',
          pointWidth: 30,
          events: {
            legendItemClick: (e) => {
              e.preventDefault();
            },
          },
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            verticalAlign() {
              return this.y === 0 ? 'bottom' : 'top';
            },
            style: {
              color: '#707070',
              textOutline: 'none',
              fontWeight: '600',
              fontSize: '11px',
              fontFamily: 'Open Sans',
            },
            y: -20,
          },
          point: {
            events: {
              click(event: any): void {
                const zoneName = objectPathExists(event, 'point.category', true) || null;
                const selectedZone = chimneyData.find((zone) => zone.zoneName === zoneName);
                const inputs: any = {
                  modalSvc,
                  shift,
                  date,
                  companyId,
                  id: selectedZone && selectedZone.zoneId,
                  category: this.category,
                };
                openModal(inputs);
              },
            },
          },
        },
      },
      tooltip: {
        backgroundColor: '#333333',
        borderColor: '#33333380',
        borderRadius: 2,
        useHTML: true,
        formatter(): string {
          return `
          <div style="width: fit-content; font-size: 13px; color: #FFFFFF;">
            <strong>${this.series.name}: ${this.y} ${shelvesLang}</strong>
          </div>`;
        },
      },
      series: buildedSeries,
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              chart: {
                className: 'small-chart',
              },
            },
          },
        ],
      },
      legend: {
        enabled: false,
      },
    };

    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
    window.dispatchEvent(new Event('resize'));
  }

  /**
   *  Build series for production by chimney
   *
   * @param chimenyProduction all production by chimney
   */
  private buildSeries(chimneyProduction: IChimneyProduction[]): any {
    const series = [];

    let i = 0;
    for (const production of chimneyProduction) {
      const data = [];

      for (let c = 0; c < i; c++) {
        data.push(0);
      }

      data.push(production.done);

      series.push({
        name: production.zoneName,
        data,
        color: 'rgba(34, 217, 131, 0.5)',
        states: {
          hover: {
            color: 'rgba(34, 217, 131, 1)',
          },
        },
      });

      i++;
    }
    return series;
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Open modal chimney
   *
   * @param shift
   * @param date
   */
  private openModalChimney(inputs: any): void {
    const componentRef = inputs.modalSvc.open(ProductionByChimneyModalComponent, {
      centered: true,
      size: 'lg',
      windowClass: 'prod-chimney',
    });
    componentRef.componentInstance.shift = inputs.shift;
    componentRef.componentInstance.date = inputs.date;
    componentRef.componentInstance.zoneId = inputs.id;
    componentRef.componentInstance.companyId = inputs.companyId;
    componentRef.componentInstance.chimneyName = inputs.category;
  }
}
